import { ActionTakenType, DocumentTemplateType } from '../api-client/autogenerated';

/**
 * Subscriber-defined custom display labels for ActionTakenType, keyed by the built-in enum value.
 *
 * DISPLAY ONLY. Documents always store the built-in enum value; these strings only change how it
 * is rendered. Nothing here may influence a filter predicate, dropdown option *set*, or workflow.
 */
export type ActionTakenLabelOverrides = Partial<Record<ActionTakenType, string>>;

/**
 * Actions a subscriber has switched off. Stored as the exception set so that "no configuration"
 * means "everything enabled". Selection only — documents already carrying a disabled action still
 * display it normally.
 */
export type DisabledActionTakenTypes = ActionTakenType[];

/** Everything a subscriber can configure about how actions are offered and displayed. */
export type ActionTakenLabelConfig = {
  labels: ActionTakenLabelOverrides;
  disabled: DisabledActionTakenTypes;
};

export const EMPTY_ACTION_TAKEN_LABEL_CONFIG: ActionTakenLabelConfig = {
  labels: {},
  disabled: [],
};

/** One titled group of actions in the subscriber admin panel. */
export type ActionTakenSection = {
  title: string;
  actions: ActionTakenType[];
};

/**
 * How the admin panel groups the configurable actions.
 *
 * The split is the real behavioural one: the first section is exactly the case group in
 * `DocumentService.applyReviewToDocument` that inserts a revision and sends the submittal back to
 * the submitter; the second is everything that closes the review out. Each section leads with the
 * outcome that cannot be switched off (see ALWAYS_ENABLED_ACTION_TAKEN_TYPES).
 */
export const ACTION_TAKEN_SECTIONS: ActionTakenSection[] = [
  {
    title: 'Review Complete - No Resubmission Required',
    actions: [
      ActionTakenType.NoExceptionsTaken,
      ActionTakenType.AmendAsNoted,
      ActionTakenType.SeeSubmittalComments,
      ActionTakenType.SeeTransmittalComments,
      ActionTakenType.ForInformationOnly,
    ],
  },
  {
    title: 'Review Complete - Resubmission Required',
    actions: [
      ActionTakenType.ReviseAndResubmit,
      ActionTakenType.Rejected,
      ActionTakenType.SubmitSpecifiedItem,
    ],
  },
];

/**
 * The actions a subscriber admin may configure — rename and enable/disable.
 *
 * Two exclusions, both because configuring them would change behaviour rather than wording:
 * - `Withdrawn` is never picked from a dropdown (it is set by the withdraw action) and is used
 *   app-wide as a soft-delete marker.
 * - `RemoveFromPackage` is a package-management operation rather than a review outcome.
 *
 * Derived from the sections rather than listed separately, so the panel's grouping and the set of
 * configurable actions cannot drift apart. Must stay in sync with the backend list and with the DB
 * CHECK constraint.
 */
export const CUSTOMIZABLE_ACTION_TAKEN_TYPES: ActionTakenType[] = ACTION_TAKEN_SECTIONS.flatMap(
  (section) => section.actions,
);

/**
 * Actions that may be renamed but never switched off. Every submittal review needs at least an
 * accept and a send-back outcome, so removing these two would leave reviewers unable to close out
 * a submittal at all. Must stay in sync with the backend.
 */
export const ALWAYS_ENABLED_ACTION_TAKEN_TYPES: ActionTakenType[] = [
  ActionTakenType.NoExceptionsTaken,
  ActionTakenType.ReviseAndResubmit,
];

export const canDisableActionTakenType = (action: ActionTakenType): boolean =>
  !ALWAYS_ENABLED_ACTION_TAKEN_TYPES.includes(action);

/**
 * The document types custom action labels apply to. Everything else — RFIs, pay applications,
 * as-builts, tasks — keeps built-in wording even when the subscriber has configured one.
 */
export const SUBMITTAL_DOCUMENT_TYPES: DocumentTemplateType[] = [
  DocumentTemplateType.Submittals,
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.SubmittalPackages,
  DocumentTemplateType.CloseoutSubmittalPackages,
];

export const isSubmittalDocumentType = (documentType?: DocumentTemplateType | null): boolean =>
  !!documentType && SUBMITTAL_DOCUMENT_TYPES.includes(documentType);

/**
 * Whether an action should be offered to a reviewer.
 *
 * Submittal-only, like the custom labels: on any other document type every action stays available
 * regardless of what the subscriber switched off. Anything not explicitly disabled is enabled, so
 * an unconfigured subscriber sees the full option set everywhere.
 */
export const isActionTakenEnabled = (
  action: ActionTakenType,
  disabled?: DisabledActionTakenTypes,
  documentType?: DocumentTemplateType | null,
): boolean => {
  if (!isSubmittalDocumentType(documentType)) return true;
  return !disabled?.includes(action);
};

export const MAX_ACTION_TAKEN_LABEL_LENGTH = 60;

/**
 * The subscriber's label for an action, or undefined to fall back to the built-in string.
 * Blank/whitespace-only overrides are treated as absent.
 */
export const getCustomActionTakenLabel = (
  action: ActionTakenType,
  overrides?: ActionTakenLabelOverrides,
  documentType?: DocumentTemplateType | null,
): string | undefined => {
  // Submittal-only: without a submittal document type there is no custom label to apply.
  if (!isSubmittalDocumentType(documentType)) return undefined;
  if (!CUSTOMIZABLE_ACTION_TAKEN_TYPES.includes(action)) return undefined;
  const custom = overrides?.[action];
  return custom && custom.trim() ? custom : undefined;
};
